import {
  RaportHSPSpecsData,
  TabDataItem,
} from "@/components";
import {
  TechnicalParameters,
  VehicleIdentification,
  EquipmentData,
  Equipment,
  EquipmentPacket,
} from "./report";
import { VehicleIdentificationData } from "@/components";

/**
 * Mapuje typ nadwozia z API na polską nazwę
 */
const mapBodyType = (bodyType: string): string => {
  const bodyTypeMap: Record<string, string> = {
    hatchback: "Hatchback",
    sedan: "Sedan",
    wagon: "Kombi",
    suv: "SUV",
    coupe: "Coupe",
    convertible: "Kabriolet",
    van: "Van",
    pickup: "Pickup",
    unit: "Jednostka",
  };
  return bodyTypeMap[bodyType.toLowerCase()] || bodyType;
};

/**
 * Mapuje typ kierownicy z API na polską nazwę
 */
const mapSteeringType = (steeringType: string): string => {
  const steeringMap: Record<string, string> = {
    S: "Standardowa",
    P: "Wspomagana hydraulicznie",
    E: "Wspomagana elektrycznie",
    EPS: "Elektryczne wspomaganie kierownicy",
  };
  return steeringMap[steeringType] || steeringType;
};

/**
 * Mapuje typ hamulców z API na polską nazwę
 */
const mapBrakeType = (brakeType: string): string => {
  const brakeMap: Record<string, string> = {
    K: "Tarczowe",
    B: "Bębnowe",
    D: "Tarczowe wentylowane",
    V: "Tarczowe pełne",
  };
  return brakeMap[brakeType] || brakeType;
};

/**
 * Mapuje typ zawieszenia z API na polską nazwę
 */
const mapSuspensionType = (suspensionType: string): string => {
  const suspensionMap: Record<string, string> = {
    VP: "MacPherson",
    WW: "Wielowahaczowe",
    ZN: "Zależne",
    NZ: "Niezależne",
    TB: "Belka skrętna",
    SP: "Sprężynowe",
  };
  return suspensionMap[suspensionType] || suspensionType;
};

/**
 * Mapuje normę emisji spalin na polską nazwę
 */
const mapEmissionStandard = (standard: string): string => {
  const emissionMap: Record<string, string> = {
    EU5: "Euro 5",
    EU6: "Euro 6",
    EU6d: "Euro 6d",
    EU4: "Euro 4",
    EU3: "Euro 3",
  };
  return emissionMap[standard] || standard;
};

/**
 * Formatuje wartość liczbową z jednostką
 */
const formatWithUnit = (value: number, unit: string): string => {
  return `${value} ${unit}`;
};

/**
 * Formatuje rozmiar opony/felgi
 */
const formatTireSize = (
  width: number,
  profile: number,
  construction: string,
  diameter: number
): string => {
  return `${width}/${profile} ${construction}${diameter}`;
};

/**
 * Mapuje kod kraju na polską nazwę
 */
const mapCountryCode = (countryCode: string): string => {
  const countryMap: Record<string, string> = {
    D: "Niemcy",
    DE: "Niemcy",
    PL: "Polska",
    CZ: "Czechy",
    SK: "Słowacja",
    F: "Francja",
    FR: "Francja",
    I: "Włochy",
    IT: "Włochy",
    E: "Hiszpania",
    ES: "Hiszpania",
    GB: "Wielka Brytania",
    UK: "Wielka Brytania",
    US: "Stany Zjednoczone",
    USA: "Stany Zjednoczone",
    J: "Japonia",
    JP: "Japonia",
    KR: "Korea Południowa",
    CN: "Chiny",
  };
  return countryMap[countryCode.toUpperCase()] || countryCode;
};

/**
 * Formatuje datę z ISO string na polski format
 */
const formatDate = (dateString: string): string | null => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

/**
 * Formatuje rok z daty ISO
 */
const formatYear = (dateString: string): string | null => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date.getFullYear().toString();
  } catch {
    return null;
  }
};

/**
 * Sprawdza czy wartość jest poprawna (nie null, nie undefined, nie pusta)
 */
const isValidValue = (value: unknown): boolean => {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    !Number.isNaN(value)
  );
};

/**
 * Mapuje parametry techniczne z API na format wymagany przez komponent HSPSpecs
 */
export const mapTechnicalParametersToSpecs = (
  technicalParams: TechnicalParameters
): RaportHSPSpecsData => {
  const {
    body,
    dimensionsAndMass,
    wheelsAndSuspension,
    fuelConsumptionAndEmissions,
  } = technicalParams;

  const result: RaportHSPSpecsData = {} as RaportHSPSpecsData;

  // Body
  if (body) {
    const bodyData: Record<string, string> = {};

    if (isValidValue(body.bodyType) && isValidValue(body.doorsCount)) {
      bodyData.bodyType = `${mapBodyType(body.bodyType)}, ${
        body.doorsCount
      }-drzwiowy`;
    }

    if (isValidValue(body.doorsCount)) {
      bodyData.doorsCount = `${body.doorsCount} (w tym klapa bagażnika)`;
    }

    if (
      body.seatsCount &&
      isValidValue(body.seatsCount.total) &&
      isValidValue(body.seatsCount.seating)
    ) {
      bodyData.seatsCount = `${body.seatsCount.total} (${body.seatsCount.seating} miejsc siedzących)`;
    }

    if (dimensionsAndMass && isValidValue(dimensionsAndMass.trunkCapacityL)) {
      bodyData.trunkCapacity = formatWithUnit(
        dimensionsAndMass.trunkCapacityL,
        "l"
      );
    }

    if (
      fuelConsumptionAndEmissions &&
      isValidValue(fuelConsumptionAndEmissions.fuelTankCapacityL)
    ) {
      bodyData.fuelTankCapacity = formatWithUnit(
        fuelConsumptionAndEmissions.fuelTankCapacityL,
        "litrów"
      );
    }

    // Dodaj body tylko jeśli ma dane
    if (Object.keys(bodyData).length > 0) {
      result.body = bodyData;
    }
  }

  // Dimensions
  if (dimensionsAndMass) {
    const dimensionsData: Record<string, string> = {};

    if (isValidValue(dimensionsAndMass.lengthMm)) {
      dimensionsData.length = formatWithUnit(dimensionsAndMass.lengthMm, "mm");
    }

    if (isValidValue(dimensionsAndMass.widthMm)) {
      dimensionsData.width = formatWithUnit(dimensionsAndMass.widthMm, "mm");
    }

    if (isValidValue(dimensionsAndMass.heightMm)) {
      dimensionsData.height = formatWithUnit(dimensionsAndMass.heightMm, "mm");
    }

    if (isValidValue(dimensionsAndMass.wheelbaseMm)) {
      dimensionsData.wheelbase = formatWithUnit(
        dimensionsAndMass.wheelbaseMm,
        "mm"
      );
    }

    if (isValidValue(dimensionsAndMass.frontTrackMm)) {
      dimensionsData.frontTrack = formatWithUnit(
        dimensionsAndMass.frontTrackMm,
        "mm"
      );
    }

    if (isValidValue(dimensionsAndMass.rearTrackMm)) {
      dimensionsData.rearTrack = formatWithUnit(
        dimensionsAndMass.rearTrackMm,
        "mm"
      );
    }

    if (isValidValue(dimensionsAndMass.curbWeightKg)) {
      dimensionsData.curbWeight = formatWithUnit(
        dimensionsAndMass.curbWeightKg,
        "kg"
      );
    }

    if (isValidValue(dimensionsAndMass.grossVehicleWeightKg)) {
      dimensionsData.maxWeight = formatWithUnit(
        dimensionsAndMass.grossVehicleWeightKg,
        "kg"
      );
    }

    if (
      isValidValue(dimensionsAndMass.trailerLoadWithBrakesKg) &&
      isValidValue(dimensionsAndMass.trailerLoadWithoutBrakesKg)
    ) {
      dimensionsData.towingCapacity = `${formatWithUnit(
        dimensionsAndMass.trailerLoadWithBrakesKg,
        "kg"
      )} (z hamulcami), ${formatWithUnit(
        dimensionsAndMass.trailerLoadWithoutBrakesKg,
        "kg"
      )} (bez hamulców)`;
    }

    // Dodaj dimensions tylko jeśli ma dane
    if (Object.keys(dimensionsData).length > 0) {
      result.dimensions = dimensionsData;
    }
  }

  // Wheels and Suspension
  if (wheelsAndSuspension) {
    const wheelsSuspensionData: Record<string, string> = {};

    if (
      isValidValue(wheelsAndSuspension.rimDiameterFrontMm) &&
      isValidValue(wheelsAndSuspension.rimWidthFrontMm)
    ) {
      wheelsSuspensionData.frontWheelSize = `${wheelsAndSuspension.rimDiameterFrontMm}" (${wheelsAndSuspension.rimWidthFrontMm}J x ${wheelsAndSuspension.rimDiameterFrontMm})`;
    }

    if (
      isValidValue(wheelsAndSuspension.rimDiameterRearMm) &&
      isValidValue(wheelsAndSuspension.rimWidthRearMm)
    ) {
      wheelsSuspensionData.rearWheelSize = `${wheelsAndSuspension.rimDiameterRearMm}" (${wheelsAndSuspension.rimWidthRearMm}J x ${wheelsAndSuspension.rimDiameterRearMm})`;
    }

    if (isValidValue(wheelsAndSuspension.tyreSizeFront)) {
      wheelsSuspensionData.frontTireSize = wheelsAndSuspension.tyreSizeFront;
    }

    if (isValidValue(wheelsAndSuspension.tyreSizeRear)) {
      wheelsSuspensionData.rearTireSize = wheelsAndSuspension.tyreSizeRear;
    }

    if (isValidValue(wheelsAndSuspension.suspensionTypeFront)) {
      wheelsSuspensionData.frontSuspension = `${mapSuspensionType(
        wheelsAndSuspension.suspensionTypeFront
      )} ze sprężynami i amortyzatorami`;
    }

    if (isValidValue(wheelsAndSuspension.suspensionTypeRear)) {
      wheelsSuspensionData.rearSuspension = `${mapSuspensionType(
        wheelsAndSuspension.suspensionTypeRear
      )} ze sprężynami i amortyzatorami`;
    }

    if (isValidValue(wheelsAndSuspension.brakeTypeFront)) {
      wheelsSuspensionData.frontBrakes = `${mapBrakeType(
        wheelsAndSuspension.brakeTypeFront
      )} hamulce`;
    }

    if (isValidValue(wheelsAndSuspension.brakeTypeRear)) {
      wheelsSuspensionData.rearBrakes = `${mapBrakeType(
        wheelsAndSuspension.brakeTypeRear
      )} hamulce`;
    }

    // Dodaj wheelsSuspension tylko jeśli ma dane
    if (Object.keys(wheelsSuspensionData).length > 0) {
      result.wheelsSuspension = wheelsSuspensionData;
    }
  }

  // Fuel and Emissions
  if (fuelConsumptionAndEmissions) {
    const fuelEmissionData: Record<string, string> = {};

    if (isValidValue(fuelConsumptionAndEmissions.cityLPer100Km)) {
      fuelEmissionData.fuelConsumptionCity = formatWithUnit(
        fuelConsumptionAndEmissions.cityLPer100Km,
        "l/100km"
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.extraUrbanLPer100Km)) {
      fuelEmissionData.fuelConsumptionHighway = formatWithUnit(
        fuelConsumptionAndEmissions.extraUrbanLPer100Km,
        "l/100km"
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.combinedLPer100Km)) {
      fuelEmissionData.fuelConsumptionCombined = formatWithUnit(
        fuelConsumptionAndEmissions.combinedLPer100Km,
        "l/100km"
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.co2GPerKm)) {
      fuelEmissionData.co2Emission = formatWithUnit(
        fuelConsumptionAndEmissions.co2GPerKm,
        "g/km"
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.emissionStandard)) {
      fuelEmissionData.emissionStandard = mapEmissionStandard(
        fuelConsumptionAndEmissions.emissionStandard
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.fuelTankCapacityL)) {
      fuelEmissionData.fuelTankCapacity = formatWithUnit(
        fuelConsumptionAndEmissions.fuelTankCapacityL,
        "litrów"
      );
    }

    if (isValidValue(fuelConsumptionAndEmissions.estimatedRangeKm)) {
      fuelEmissionData.range = formatWithUnit(
        fuelConsumptionAndEmissions.estimatedRangeKm,
        "km"
      );
    }

    // Dodaj fuelEmission tylko jeśli ma dane
    if (Object.keys(fuelEmissionData).length > 0) {
      result.fuelEmission = fuelEmissionData;
    }
  }

  return result;
};

/**
 * Tworzy dane specyfikacji technicznych na podstawie parametrów z API
 */
export const createSpecsData = (
  technicalParams?: TechnicalParameters
): RaportHSPSpecsData => {
  if (!technicalParams) {
    return {} as RaportHSPSpecsData;
  }

  return mapTechnicalParametersToSpecs(technicalParams);
};

/**
 * Mapuje dane identyfikacyjne pojazdu z API na format wymagany przez komponent HSPVehicleIdentification
 */
export const mapVehicleIdentificationData = (
  vehicleIdentification: VehicleIdentification
): VehicleIdentificationData => {
  const result: VehicleIdentificationData = {} as VehicleIdentificationData;

  // Parsowanie marki i modelu
  if (isValidValue(vehicleIdentification.brandAndModel)) {
    const [brand, ...modelParts] =
      vehicleIdentification.brandAndModel.split(" ");
    if (brand) {
      result.brand = brand;
    }
    const model = modelParts.join(" ");
    if (model) {
      result.model = model;
    }
  }

  if (isValidValue(vehicleIdentification.generation)) {
    result.generation = vehicleIdentification.generation;
  }

  if (isValidValue(vehicleIdentification.version)) {
    result.version = vehicleIdentification.version;
  }

  if (isValidValue(vehicleIdentification.productionYear)) {
    result.productionYear = vehicleIdentification.productionYear.toString();
  }

  if (isValidValue(vehicleIdentification.modelYear)) {
    result.modelYear = vehicleIdentification.modelYear.toString();
  }

  if (isValidValue(vehicleIdentification.vin)) {
    result.vin = vehicleIdentification.vin;
  }

  if (isValidValue(vehicleIdentification.countryOfManufacture)) {
    result.countryOfOrigin = mapCountryCode(
      vehicleIdentification.countryOfManufacture
    );
  }

  if (isValidValue(vehicleIdentification.faceliftDate)) {
    const year = formatYear(vehicleIdentification.faceliftDate);
    if (year) {
      result.faceliftYear = year;
    }
  }

  if (isValidValue(vehicleIdentification.productionStartDate)) {
    const date = formatDate(vehicleIdentification.productionStartDate);
    if (date) {
      result.productionStartDate = date;
    }
  }

  if (isValidValue(vehicleIdentification.productionEndDate)) {
    const date = formatDate(vehicleIdentification.productionEndDate);
    if (date) {
      result.productionEndDate = date;
    }
  }

  return result;
};

/**
 * Tworzy dane identyfikacyjne pojazdu na podstawie parametrów z API
 */
export const createVehicleIdentificationData = (
  vehicleIdentification?: VehicleIdentification
): VehicleIdentificationData => {
  if (!vehicleIdentification) {
    return {} as VehicleIdentificationData;
  }

  return mapVehicleIdentificationData(vehicleIdentification);
};

/**
 * Mapuje kategorię wyposażenia na polską nazwę
 */
const mapEquipmentCategory = (category: string | undefined | null): string => {
  // Jeśli brak kategorii, zwróć "Inne"
  if (!category || category.trim() === "") {
    return "Inne";
  }

  const categoryMap: Record<string, string> = {
    Safety: "Bezpieczeństwo",
    Comfort: "Komfort",
    Interior: "Wnętrze",
    "Exterior appearance": "Wygląd zewnętrzny",
    "Multimedia and communication": "Multimedia i komunikacja",
    Functionality: "Funkcjonalność",
    "Mechanical systems": "Układy mechaniczne",
  };
  return categoryMap[category] || category;
};

/**
 * Formatuje cenę w PLN
 */
const formatPrice = (price: number): string => {
  return `${price.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} PLN`;
};

/**
 * Mapuje wyposażenie standardowe na format TabDataItem
 */
export const mapStandardEquipmentToTabData = (
  equipmentData?: EquipmentData
): TabDataItem[] => {
  if (!equipmentData?.standard) {
    return [];
  }

  const tabData: TabDataItem[] = [];
  const { equipments = [], packets = [] } = equipmentData.standard;

  // Grupowanie wyposażenia według kategorii
  const groupedEquipments: Record<string, Equipment[]> = {};

  // Dodaj wyposażenie z pakietów
  packets.forEach((packet) => {
    if (!packet.equipments || packet.equipments.length === 0) {
      return;
    }

    packet.equipments.forEach((equipment) => {
      const category = mapEquipmentCategory(equipment.category);
      if (!groupedEquipments[category]) {
        groupedEquipments[category] = [];
      }
      groupedEquipments[category].push({
        ...equipment,
        name: `${equipment.name} (${packet.name})`,
      });
    });
  });

  // Dodaj pojedyncze wyposażenie
  equipments.forEach((equipment) => {
    const category = mapEquipmentCategory(equipment.category);
    if (!groupedEquipments[category]) {
      groupedEquipments[category] = [];
    }
    groupedEquipments[category].push(equipment);
  });

  // Konwertuj na format TabDataItem
  Object.entries(groupedEquipments).forEach(([category, items]) => {
    items.forEach((item) => {
      tabData.push({
        title: category,
        label: item.name,
        value: item.code,
        icon: getCategoryIcon(item.category),
      });
    });
  });

  return tabData;
};

/**
 * Mapuje wyposażenie opcjonalne na format TabDataItem
 */
export const mapOptionalEquipmentToTabData = (
  equipmentData?: EquipmentData
): TabDataItem[] => {
  if (!equipmentData?.optional) {
    return [];
  }

  const tabData: TabDataItem[] = [];
  const { equipments = [], packets = [] } = equipmentData.optional;

  // Grupowanie wyposażenia według kategorii
  const groupedEquipments: Record<string, (Equipment | EquipmentPacket)[]> = {};

  // Dodaj pakiety wyposażenia
  packets.forEach((packet) => {
    const category = "Pakiety wyposażenia";
    if (!groupedEquipments[category]) {
      groupedEquipments[category] = [];
    }
    groupedEquipments[category].push(packet);
  });

  // Dodaj pojedyncze wyposażenie opcjonalne
  equipments.forEach((equipment) => {
    const category = mapEquipmentCategory(equipment.category);
    if (!groupedEquipments[category]) {
      groupedEquipments[category] = [];
    }
    groupedEquipments[category].push(equipment);
  });

  // Konwertuj na format TabDataItem
  Object.entries(groupedEquipments).forEach(([category, items]) => {
    items.forEach((item) => {
      const isPacket = "equipments" in item;
      // const price =
      //   item.catalogPrice !== undefined && item.catalogPrice !== null
      //     ? formatPrice(item.catalogPrice)
      //     : "Brak danych";
      // const currentPrice =
      //   item.currentMarketPrice !== undefined && item.currentMarketPrice !== null
      //     ? formatPrice(item.currentMarketPrice)
      //     : "Brak danych";

      tabData.push({
        title: category,
        label: item.name,
        // value: `Cena katalogowa: ${price} | Aktualna wartość: ${currentPrice}`,
        value: item.code,
        icon: isPacket
          ? "plus-circle"
          : getCategoryIcon((item as Equipment).category),
      });

      // Jeśli to pakiet, dodaj również jego zawartość
      if (isPacket && (item as EquipmentPacket).equipments) {
        (item as EquipmentPacket).equipments.forEach((equipment) => {
          tabData.push({
            title: category,
            label: `  └─ ${equipment.name}`,
            value: "Element pakietu",
            icon: getCategoryIcon(equipment.category),
          });
        });
      }
    });
  });

  return tabData;
};

/**
 * Zwraca ikonę dla kategorii wyposażenia
 */
const getCategoryIcon = (
  category: string | undefined | null
):
  | "i"
  | "shield"
  | "handshake"
  | "car"
  | "network-nodes"
  | "comfort"
  | "trophy"
  | "play-box"
  | "plus-circle" => {
  // Jeśli brak kategorii, zwróć domyślną ikonę
  if (!category || category.trim() === "") {
    return "i";
  }

  const iconMap: Record<
    string,
    | "i"
    | "shield"
    | "handshake"
    | "car"
    | "network-nodes"
    | "comfort"
    | "trophy"
    | "play-box"
    | "plus-circle"
  > = {
    Safety: "shield",
    Comfort: "comfort",
    Interior: "trophy",
    "Exterior appearance": "car",
    "Multimedia and communication": "play-box",
    Functionality: "plus-circle",
    "Mechanical systems": "network-nodes",
  };

  return iconMap[category] || "i";
};

/**
 * Tworzy dane wyposażenia standardowego na podstawie danych z API
 */
export const createStandardEquipmentData = (
  equipmentData?: EquipmentData
): TabDataItem[] => {
  return mapStandardEquipmentToTabData(equipmentData);
};

/**
 * Tworzy dane wyposażenia opcjonalnego na podstawie danych z API
 */
export const createOptionalEquipmentData = (
  equipmentData?: EquipmentData
): TabDataItem[] => {
  return mapOptionalEquipmentToTabData(equipmentData);
};
